import { pointer, Toast } from '@byecode/ui'
import type { AvatarSize } from '@lighthouse/shared'
import { avatarSize, tokenStorage, useTokenRefreshManager, useUploadTokenRefresh } from '@lighthouse/shared'
import { useItemErrorListener, useItemFinishListener, useUploadyContext } from '@rpldy/shared-ui'
import UploadDropZone from '@rpldy/upload-drop-zone'
import { useRequestPreSend } from '@rpldy/uploady'
import React, { useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'

import { RoundAvatar } from '../RoundAvatar'

interface UploadSpaceAvatarProps {
    children?: React.ReactNode
    onUpdateImg: (imgSrc: string) => void
    border?: boolean
    size?: AvatarSize
    name?: string
    img?: string
    type?: 'img' | 'icon'
    fontSize?: number
    background?: string
    disable?: boolean
}

const Avatar = styled.div<{ size: number; border?: boolean; fontSize?: number }>`
    width: ${({ size }) => `${size}px`};
    height: ${({ size }) => `${size}px`};
    font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : '14px')};
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    ${({ border }) =>
        border &&
        css`
            border: 1px solid var(--color-gray-200);
            border-radius: 6px;
        `}

    ${pointer}
`

export const UploadSpaceAvatar: React.FC<UploadSpaceAvatarProps> = ({
    img,
    type = 'img',
    onUpdateImg,
    name,
    disable = false,
    fontSize,
    background,
    size = 'xLarge'
}) => {
    const uploady = useUploadyContext()
    const tokenRefreshManager = useTokenRefreshManager()
    const spaceSize = useMemo(() => avatarSize[size], [size])

    // 动态注入 Authorization token
    useRequestPreSend(({ items, options }) => {
        const token = tokenStorage.getAccessToken()

        return {
            items,
            options: {
                ...options,
                destination: {
                    ...options.destination,
                    headers: {
                        ...options.destination?.headers,
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    }
                }
            }
        }
    })

    // 处理 token 刷新后的重试
    useUploadTokenRefresh()

    useItemFinishListener(item => {
        const { uploadResponse } = item

        onUpdateImg(uploadResponse.data.content.url)
    })

    useItemErrorListener(async item => {
        // 🔑 处理 401 错误
        if (item.uploadStatus === 401) {
            if (!tokenRefreshManager) {
                Toast.error('认证失败，请重新登录')
                return
            }

            try {
                // 刷新 token
                await tokenRefreshManager.handleUpload401(item.id)
                // 触发重试
                const { uploadManageEvents } = await import('@lighthouse/shared/src/components/UploadManage/hooks/events')
                uploadManageEvents.emit('retry-item', { itemId: item.id })
            } catch {
                Toast.error('上传失败，请重试')
            }
            return
        }

        // 其他错误
        Toast.error('上传失败，请重试')
    })

    const handleUpload = useCallback(() => {
        if (disable) {
            return
        }
        uploady.showFileUpload()
    }, [disable, uploady])

    return (
        <UploadDropZone>
            <Avatar size={spaceSize} fontSize={fontSize} onClick={handleUpload}>
                <RoundAvatar radius="8px" icon={img} name={name || ''} type={type} size={size} background={background} />
            </Avatar>
        </UploadDropZone>
    )
}
